import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from '../assets/Login.module.css';

// Image assets from Figma
const img = "/images/icons/login-cancel.svg";
const img1 = "/images/icons/login-close.svg";
const imgVector = "/images/icons/login-vector.svg";
const img2 = "/images/icons/login-email.svg";
const img3 = "/images/icons/login-password.svg";
const img4 = "/images/icons/login-eye.svg";
const img5 = "/images/icons/login-check.svg";
const img6 = "/images/icons/login-google.svg";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCookie, setShowCookie] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const { login, isContractor, getContractorContactId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where the user was originally heading before ProtectedRoute bounced them here
  // (e.g. a ticket link from an email). Captured during the first render because
  // the effect below clears location.state, which would otherwise take this with
  // it and leave the user on their default dashboard after logging in.
  const redirectTargetRef = useRef(location.state?.from || null);

  // Handle success message and auto-fill from registration/forgot-password flow
  useEffect(() => {
    // Check for query parameter messages (from guardian retention, etc.)
    const searchParams = new URLSearchParams(location.search);
    const queryMessage = searchParams.get('message');
    const queryType = searchParams.get('type');

    if (queryMessage) {
      if (queryType === 'success') {
        setSuccess(queryMessage);
        console.log('✅ Success message from query param:', queryMessage);
      } else if (queryType === 'error') {
        setError(queryMessage);
        console.log('❌ Error message from query param:', queryMessage);
      } else {
        setSuccess(queryMessage); // Default to success for 'info' or unspecified
        console.log('ℹ️ Info message from query param:', queryMessage);
      }

      // Clear query parameters from URL
      navigate(location.pathname, { replace: true });
    }

    if (location.state?.message) {
      setSuccess(location.state.message);
    }

    // Auto-fill email and password from registration
    if (location.state?.email) {
      setEmail(location.state.email);
      console.log('✅ Email auto-filled from registration:', location.state.email);
    }

    if (location.state?.password) {
      setPassword(location.state.password);
      console.log('✅ Password auto-filled from registration');
    }

    // Clear the state to prevent showing the message again on refresh
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.search, navigate, location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await login(email, password);
      if (result.success) {
        // Small delay to ensure auth context is updated
        setTimeout(() => {
          // If the user was redirected here from a protected route, send them back to it
          const from = redirectTargetRef.current || location.state?.from;
          const fromPath = from?.pathname;
          const fromSearch = from?.search || '';
          if (fromPath && fromPath !== '/login') {
            navigate(`${fromPath}${fromSearch}`, { replace: true });
            return;
          }

          // Laravel equivalent: Check user role and redirect accordingly
          if (isContractor()) {
            const contractorId = getContractorContactId();
            navigate(`/contractors/contacts/${contractorId}/calendar`);
          } else {
            navigate('/dashboard');
          }
        }, 100);
      } else {
        // Show the actual error message from backend
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Implement Google sign-in logic here
    console.log('Google sign-in clicked');
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleSignUp = () => {
    navigate('/register');
  };

  return (
    <div className={styles.loginPage}>
      {/* Modal Container */}
      <div className={styles.modalContainer}>
        <div className={styles.modal}>
          {/* Modal Header */}
          {/* <div className={styles.modalHeader}>
            <div className={styles.cancelIcon}>
              <img src={img} alt="cancel" />
            </div>
            <div className={styles.modalTitle}>Login</div>
            <div className={styles.closeIcon}>
              <img src={img1} alt="close" />
            </div>
          </div> */}

          {/* Modal Content */}
          <div className={styles.modalContent}>
            {/* Logo */}
            <div className={styles.logoContainer}>
              <img src={imgVector} alt="logo" />
            </div>

            {/* Welcome Text */}
            <div className={styles.welcomeSection}>
              <h1 className={styles.welcomeTitle}>Welcome back!</h1>
              <p className={styles.welcomeDescription}>
                Please enter your details below to log in.
              </p>
            </div>

            {/* Success Message */}
            {success && (
              <div className={styles.successMessage}>
                {success}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className={styles.loginForm}>
              <div className={styles.formFields}>
                {/* Email Field */}
                <div className={styles.inputContainer}>
                  <div className={styles.inputIcon}>
                    <img src={img2} alt="user" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@example.com"
                    className={styles.formInput}
                    required
                  />
                </div>

                {/* Password Field */}
                <div className={styles.inputContainer}>
                  <div className={styles.inputIcon}>
                    <img src={img3} alt="lock" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    className={styles.formInput}
                    required
                  />
                  <div 
                    className={styles.eyeIcon}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img src={img4} alt="eye" />
                  </div>
                </div>
              </div>

              {/* Remember Me and Forgot Password */}
              <div className={styles.optionsRow}>
                <div className={styles.rememberMe}>
                  <label htmlFor="remember" className={styles.rememberLabel}>
                    <div className={styles.checkbox}>
                      <input
                        type="checkbox"
                        id="remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className={styles.checkboxInput}
                      />
                      <div className={styles.checkboxIndicator}>
                        {rememberMe && (
                          <div className={styles.checkIcon}>
                            <img src={img5} alt="check" />
                          </div>
                        )}
                      </div>
                    </div>
                    Remember me
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className={styles.forgotPassword}
                >
                  Forgot password
                </button>
              </div>

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={styles.continueButton}
                >
                  <span>{isLoading ? 'Logging in...' : 'Continue'}</span>
                </button>

                {/* Google Sign In Button - Hidden */}
                {/* <div className={styles.googleButton} onClick={handleGoogleSignIn}>
                  <div className={styles.googleIcon}>
                    <img src={img6} alt="Google" />
                  </div>
                  <span>Sign in with Google</span>
                </div> */}
              </div>

              {/* Sign Up Link */}
              <div className={styles.signUpRow}>
                <span className={styles.signUpText}>
                  Don't have an account? <span className={styles.signUpLink} onClick={handleSignUp}>Sign up</span>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Cookie Banner */}
      {showCookie && (
        <div style={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          width: '100%',
          background: '#fff',
          color: '#17264b',
          fontWeight: 700,
          fontSize: 18,
          fontFamily: 'Montserrat, Arial, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          zIndex: 1000,
          minHeight: 56,
        }}>
          <span style={{ margin: '0 16px' }}>
            Global Guardians uses cookies to ensure you get the best experience on our website.
          </span>
          <button
            style={{
              background: '#36bba5',
              color: '#fff',
              fontWeight: 700,
              border: 'none',
              borderRadius: 4,
              padding: '8px 18px',
              margin: '0 8px',
              fontSize: 16,
              cursor: 'pointer',
              letterSpacing: 1,
              fontFamily: 'Montserrat, Arial, sans-serif',
            }}
            onClick={() => window.open('https://www.global-guardians.co.uk/cookies', '_blank')}
          >
            + LEARN MORE
          </button>
          <button
            style={{
              background: '#36bba5',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              fontSize: 20,
              margin: '0 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Montserrat, Arial, sans-serif',
            }}
            onClick={() => setShowCookie(false)}
            aria-label="Close cookie banner"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default Login;
